import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";

import { ListLoadingState } from "../../components/ListLoadingState";
import { OpenShiftCardContainer } from "../../Shift/Open/CardContainer";
import { convertShiftToSlotId } from "../../Shift/Open/useGetShiftsSlotV2";
import { useOpenShiftListDataContext } from "../../Shift/Open/useOpenShiftListDataContext";
import { useVirtualShiftListContext } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { ShiftVirtualizedList } from "../../Shift/Open/VirtualizedList";
import { PriorityShiftsEmptyState } from "./EmptyState";

interface PriorityShiftsListProps {
  scrollRef: React.RefObject<HTMLDivElement>;
}

export function PriorityShiftsList(props: PriorityShiftsListProps) {
  const { scrollRef } = props;

  const { listRef, virtualShiftListItems } = useVirtualShiftListContext();

  const isListEmpty = virtualShiftListItems.length === 0;

  const {
    onClickOpenShift,
    workplacesMap,
    shiftsBlockingRequirementsContextMap,
    isOpenShiftsLoading,
    isOpenShiftsError,
    isOpenShiftsSuccess,
    shiftsSlotsMap,
    friendsMap,
    recentColleaguesMap,
    handleRangeChanged,
    favoriteWorkplaceIds,
    shiftAuctionsMap,
    canLoadMore,
    loadMore,
    isLoadingMore,
  } = useOpenShiftListDataContext();

  if (isOpenShiftsError) {
    return <Text align="center">Error loading priority shifts</Text>;
  }

  if (isOpenShiftsLoading) {
    return (
      <ListLoadingState
        texts={[
          "Finding you the best-paying shifts",
          "Analyzing shift opportunities just for you",
          "Your perfect shift is just a moment away",
          "Crunching the numbers to maximize your earnings",
          "Digging deep into facility schedules",
          "Searching high and low for the best shifts",
          "Unlocking your next great opportunity",
        ]}
      />
    );
  }

  if (isOpenShiftsSuccess && isListEmpty) {
    return <PriorityShiftsEmptyState />;
  }

  return (
    <ShiftVirtualizedList
      ref={listRef}
      scrollRef={scrollRef}
      items={virtualShiftListItems}
      isLoadingMore={isLoadingMore}
      renderItem={(item) => {
        if (item.type !== "open-shift") {
          // Skip non-open-shift items (like in-app ads)
          return null;
        }

        const shift = item;
        const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
        if (!isDefined(workplace)) {
          return null;
        }

        const shiftAuctionId = shift.relationships.shiftAuction?.data.id;
        const shiftAuction = shiftAuctionId ? shiftAuctionsMap.get(shiftAuctionId) : undefined;

        return (
          <OpenShiftCardContainer
            key={shift.id}
            workers={
              shiftsSlotsMap
                .get(
                  convertShiftToSlotId({
                    start: shift.attributes.start,
                    end: shift.attributes.end,
                    facilityId: workplace.id,
                  })
                )
                ?.shiftAssignments.map(({ worker }) => worker) ?? []
            }
            shift={shift}
            workplace={workplace}
            isFavoriteWorkplace={favoriteWorkplaceIds.has(workplace.id)}
            blockingRequirementsContext={(() => {
              const context = shiftsBlockingRequirementsContextMap.get(shift.id);
              return context === null ? undefined : context;
            })()}
            friendsMap={friendsMap}
            recentColleaguesMap={recentColleaguesMap}
            shiftAuction={shiftAuction}
            onClick={({ offer }) => {
              onClickOpenShift({ shift, workplace, offer, shiftAuction });
            }}
          />
        );
      }}
      onEndReached={() => {
        if (canLoadMore) {
          loadMore();
        }
      }}
      onRangeChanged={handleRangeChanged}
    />
  );
}
